import type { JSX } from 'react';
import type { Locale } from '../i18n';
import { widgetText } from './copy';
import { ErrorMessage } from './ErrorMessage';
import { MerchantReturnLink } from './merchantReturn';
import { PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS, STACK_CLASS } from './styles';

/**
 * Fallos que NO se arreglan repitiendo la misma peticion.
 *
 * <p>La sesion, o la credencial con la que se pide, ya no existe: el mismo GET dara el mismo error
 * para siempre. Ofrecer «Reintentar» ahi es una promesa falsa, y la unica salida honesta es volver
 * a la tienda.
 *
 * <p>La lista es CERRADA y el resto se considera reintentable a proposito: un codigo que esta build
 * no conoce puede ser perfectamente transitorio, y repetir un GET no cuesta nada ni cobra nada. El
 * caso que trajo esta pantalla --un fallo de red-- es justo de los que se arreglan solos.
 */
const PERMANENT_CODES: ReadonlySet<string> = new Set<string>([
  'invalid_api_key',
  'invalid_client_secret',
  'insufficient_permissions',
  'resource_not_found',
  'session_invalid',
  'intent_expired',
  'embed_origin_denied',
]);

/** Si repetir la carga puede cambiar algo. Ver {@link PERMANENT_CODES}. */
export function retryMakesSense(code: string): boolean {
  return !PERMANENT_CODES.has(code);
}

export interface SessionErrorScreenProps {
  /** `code` del problem+json que tumbo la carga; es el contrato estable por el que se decide. */
  code: string;
  /** Copia de respaldo del primer renglon, ya localizada (ver ErrorMessage). */
  message: string;
  /** `detail` del servidor, para el comercio. */
  detail: string | null;
  locale: Locale;
  /** Vuelve a pedir el intent, sin recargar la pagina ni perder la sesion en memoria. */
  onRetry: () => void;
  /**
   * `merchant_return_url` RECORDADO de una carga anterior de esta misma pestana.
   *
   * <p>No puede salir del intent: el intent es justo lo que no llego. Sin nada recordado no se
   * pinta salida --adivinarla con el `referrer` o con `history.back()` es peor que no ofrecerla, ver
   * {@link MerchantReturnLink}--.
   */
  merchantReturnUrl?: string | undefined;
  /** Si el widget vive en un iframe del comercio. Empotrado esta pantalla no anade nada. */
  embedded?: boolean | undefined;
}

/**
 * La sesion que NO se pudo cargar, con salida.
 *
 * <p>Era la unica pantalla del checkout sin ninguna: sin intent no hay selector, ni estado terminal,
 * ni `merchant_return_url` que ofrecer, asi que quedaba el aviso rojo y nada mas. En modo pagina el
 * checkout ES la pestana, de modo que al comprador solo le quedaba cerrarla a mano --y si venia de
 * pagar en la pasarela, cerrarla justo despues de pagar, sin saber si el pago se hizo--. Reportado
 * desde QA el 2026-09-08 sobre un fallo de red.
 *
 * <p>Son dos salidas y ninguna adivina nada: repetir la carga, que es lo que arregla el fallo que lo
 * destapo, y volver a la tienda con la URL que el comercio ya declaro y que se recordo cuando el
 * intent si cargaba.
 *
 * <p><strong>Empotrado no se anade ninguna de las dos, y no es un descuido.</strong> El fallo ya
 * viajo al comercio como `apipay:error` y el desenlace es UNO SOLO: el widget no puede desdecirse.
 * Un reintento que saliera bien cobraria de verdad sin que el comercio llegara a enterarse --su
 * `onError` ya se ejecuto-- y el comprador acabaria pagando un pedido que su tienda da por fallido.
 * La salida tampoco es nuestra: quien rodea al iframe es el comercio, que ya sabe que esto murio y
 * es quien sabe cerrar su modal.
 */
export function SessionErrorScreen({
  code,
  message,
  detail,
  locale,
  onRetry,
  merchantReturnUrl,
  embedded,
}: SessionErrorScreenProps): JSX.Element {
  const aviso = <ErrorMessage code={code} message={message} detail={detail} locale={locale} />;

  if (embedded === true) {
    return aviso;
  }

  const puedeReintentar = retryMakesSense(code);

  return (
    <div className={STACK_CLASS} data-testid="session-error">
      {aviso}
      {puedeReintentar ? (
        <button
          type="button"
          data-testid="retry-session"
          className={PRIMARY_BUTTON_CLASS}
          onClick={onRetry}
        >
          {widgetText(locale, 'common.retry')}
        </button>
      ) : null}
      {/* La vuelta es la accion principal cuando reintentar no puede arreglar nada. */}
      <MerchantReturnLink
        merchantReturnUrl={merchantReturnUrl}
        locale={locale}
        className={puedeReintentar ? SECONDARY_BUTTON_CLASS : PRIMARY_BUTTON_CLASS}
      />
    </div>
  );
}
